import { useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import ProvinceSelect, { regionName } from '../ProvinceSelect';
import { formatCalendarDate } from '../../lib/calendar-date';
import { TAX_SYSTEMS, TAX_SYSTEM_LABELS, defaultRateLabels, isTaxSystem, requiredRates } from '../../constants/tax-systems';
import {
  validateTaxRateForm,
  addFormForJurisdiction,
  type StandardTaxRate,
  type TaxRateFormValues,
  type TaxRateFormField,
  type TaxRateRow,
  type TaxRateWrite,
} from '../../lib/company-tax-rates';

/**
 * Add / change / correct one per-jurisdiction tax row.
 *
 * ── THE THREE MODES ARE THE POINT ───────────────────────────────────────────
 *
 * `PUT /settings/tax-rates` upserts on `(jurisdiction, effective_from)`, so
 * ONE endpoint performs two operations a business cares about very
 * differently, and which one it performs depends entirely on whether the date
 * moved. A single "Edit" dialog would let the office correct history by
 * accident, or leave an orphan row behind while thinking it had edited one.
 *
 *   add       a jurisdiction with no rows yet. Both keys open.
 *   change    a rate change: jurisdiction FIXED, date open and BLANK. The
 *             existing rows stay and keep answering for work dated before the
 *             new start date.
 *   correct   an existing row: jurisdiction fixed, DATE READ-ONLY. Moving the
 *             date here would silently mint a second row and leave the
 *             original in place, which is 'change' wearing the wrong label.
 *
 * WHAT IS PREFILLED, AND FROM WHERE. `add`: picking a Canadian province or
 * territory fills its statutory system, rates and labels from
 * `GET /settings/standard-tax-rates` — the same table tenant creation uses —
 * and the start date opens on 1 January of the tenant's year. Every field
 * stays editable. A US state fills nothing (its rate is local). `change`
 * opens blank apart from the jurisdiction: the new rate IS the question.
 * `correct` shows the row's own stored values, because that is the row being
 * corrected.
 */
export type TaxRateModalMode = 'add' | 'change' | 'correct';

const inputCls = 'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50';
const errorCls = 'border-red-500/60';

interface Props {
  mode: TaxRateModalMode;
  initialValues: TaxRateFormValues;
  country: 'CA' | 'US';
  /** The row being corrected; null for add and change. */
  existingRow: TaxRateRow | null;
  /** `add` only: the statutory table, undefined while loading or when it failed. */
  standardRates?: readonly StandardTaxRate[];
  standardRatesFailed?: boolean;
  saving: boolean;
  onCancel: () => void;
  onSubmit: (payload: TaxRateWrite) => void;
}

const TITLES: Record<TaxRateModalMode, string> = {
  add: 'Add jurisdiction',
  change: 'Rate change',
  correct: 'Correct this row',
};

export default function TaxRateRowModal({
  mode, initialValues, country, existingRow, standardRates, standardRatesFailed, saving, onCancel, onSubmit,
}: Props) {
  const [values, setValues] = useState<TaxRateFormValues>(initialValues);
  const [error, setError] = useState<{ field: TaxRateFormField; message: string } | null>(null);

  const set = <K extends TaxRateFormField>(key: K, value: TaxRateFormValues[K]) => {
    setValues(prev => ({ ...prev, [key]: value }));
    setError(prev => (prev?.field === key ? null : prev));
  };

  // Picking a province in `add` replaces the system, rates and labels with its
  // standard ones (or clears them, for a state with none), and keeps the date.
  const pickJurisdiction = (code: string) => {
    setValues(prev => addFormForJurisdiction(code, standardRates, prev.effective_from));
    setError(null);
  };

  const submit = () => {
    const result = validateTaxRateForm(values);
    if (!result.ok) {
      setError({ field: result.field, message: result.error });
      return;
    }
    onSubmit(result.payload);
  };

  const system = isTaxSystem(values.tax_system) ? values.tax_system : null;
  const need = system ? requiredRates(system) : { rate1: false, rate2: false };
  const placeholders = system ? defaultRateLabels(system) : { label1: null, label2: null };
  const name = regionName(values.jurisdiction);
  const heading = mode === 'add'
    ? TITLES.add
    : `${TITLES[mode]} — ${values.jurisdiction}${name === values.jurisdiction ? '' : ` (${name})`}`;

  const fieldError = (field: TaxRateFormField) => (error?.field === field ? error.message : null);
  const cls = (field: TaxRateFormField) => `${inputCls} ${fieldError(field) ? errorCls : ''}`;

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div role="dialog" aria-modal="true" aria-label={heading} className="bg-fieldiq-card border border-fieldiq-border rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-fieldiq-border">
          <h3 className="text-lg font-bold text-white">{heading}</h3>
        </div>

        <div className="px-5 py-4 space-y-4">
          {mode === 'change' && (
            <p className="text-sm text-fieldiq-text">
              Starts a new rate for {values.jurisdiction} from the date you choose. Earlier work keeps the old rate.
            </p>
          )}

          {mode === 'correct' && existingRow && (
            <div className="flex gap-2 items-start rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-sm text-white">
              <AlertTriangle size={16} className="mt-0.5 shrink-0 text-fieldiq-accent-signal" aria-hidden="true" />
              <p>
                This also changes documents already dated under this rate (from {formatCalendarDate(existingRow.effective_from) ?? existingRow.effective_from}).
              </p>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-white block mb-1" htmlFor="tax-rate-jurisdiction">Jurisdiction</label>
            {mode === 'add' ? (
              /* ProvinceSelect EMITS THE TWO-LETTER CODE. That is the fold at
                 the client door: `company_tax_rates.jurisdiction` is
                 VARCHAR(2), and Postgres applies the column length before the
                 normalising trigger runs, so a full name is a 22001 rather
                 than something the trigger folds to 'SK'. */
              <ProvinceSelect
                id="tax-rate-jurisdiction"
                value={values.jurisdiction}
                onChange={pickJurisdiction}
                country={country}
                className={cls('jurisdiction')}
                placeholder="Select a province or state..."
              />
            ) : (
              /* A <label htmlFor> does not name a <div>, so the read-only
                 display carries its own accessible name. */
              <div id="tax-rate-jurisdiction" aria-label="Jurisdiction" className={`${inputCls} bg-fieldiq-card/60`}>
                {values.jurisdiction}{name === values.jurisdiction ? '' : ` — ${name}`}
              </div>
            )}
            {fieldError('jurisdiction') && <p className="text-xs text-red-400 mt-1">{fieldError('jurisdiction')}</p>}
            {mode === 'add' && country === 'CA' && standardRatesFailed && (
              <p className="text-xs text-fieldiq-text mt-1">Standard rates didn't load — enter the rates yourself.</p>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-white block mb-1" htmlFor="tax-rate-effective-from">Effective from</label>
            {mode === 'correct' ? (
              <>
                {/* READ-ONLY ON PURPOSE. The date is half the unique key, so
                    changing it here would create a second row and leave this
                    one behind rather than moving it. */}
                <div id="tax-rate-effective-from" aria-label="Effective from" className={`${inputCls} bg-fieldiq-card/60`}>
                  {formatCalendarDate(values.effective_from) ?? values.effective_from}
                </div>
                <p className="text-xs text-fieldiq-text mt-1">To change the date, use Rate change.</p>
              </>
            ) : (
              <>
                {/* type="date" hands back 'YYYY-MM-DD' as a STRING. It is never
                    put through `new Date()` — a calendar day parsed as an
                    instant lands on the previous day west of Greenwich. */}
                <input
                  id="tax-rate-effective-from"
                  type="date"
                  className={cls('effective_from')}
                  value={values.effective_from}
                  onChange={e => set('effective_from', e.target.value)}
                />
                <p className="text-xs text-fieldiq-text mt-1">First day this rate applies.</p>
              </>
            )}
            {fieldError('effective_from') && <p className="text-xs text-red-400 mt-1">{fieldError('effective_from')}</p>}
          </div>

          <div>
            <label className="text-sm font-medium text-white block mb-1" htmlFor="tax-rate-system">Tax system</label>
            <select id="tax-rate-system" className={cls('tax_system')} value={values.tax_system} onChange={e => set('tax_system', e.target.value)}>
              <option value="">Select a tax system...</option>
              {TAX_SYSTEMS.map(s => <option key={s} value={s}>{TAX_SYSTEM_LABELS[s]}</option>)}
            </select>
            {fieldError('tax_system') && <p className="text-xs text-red-400 mt-1">{fieldError('tax_system')}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="tax-rate-1">
                Rate 1 (%){need.rate1 && <span className="text-fieldiq-accent-signal"> *</span>}
              </label>
              <input
                id="tax-rate-1"
                type="number"
                step="0.001"
                min="0"
                max="100"
                className={cls('tax_rate_1')}
                value={values.tax_rate_1}
                onChange={e => set('tax_rate_1', e.target.value)}
              />
              {fieldError('tax_rate_1') && <p className="text-xs text-red-400 mt-1">{fieldError('tax_rate_1')}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="tax-rate-1-label">Rate 1 label</label>
              <input
                id="tax-rate-1-label"
                className={cls('tax_rate_1_label')}
                maxLength={50}
                placeholder={placeholders.label1 ?? 'No label'}
                value={values.tax_rate_1_label}
                onChange={e => set('tax_rate_1_label', e.target.value)}
              />
              {fieldError('tax_rate_1_label') && <p className="text-xs text-red-400 mt-1">{fieldError('tax_rate_1_label')}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="tax-rate-2">
                Rate 2 (%){need.rate2 && <span className="text-fieldiq-accent-signal"> *</span>}
              </label>
              <input
                id="tax-rate-2"
                type="number"
                step="0.001"
                min="0"
                max="100"
                className={cls('tax_rate_2')}
                value={values.tax_rate_2}
                onChange={e => set('tax_rate_2', e.target.value)}
              />
              {fieldError('tax_rate_2') && <p className="text-xs text-red-400 mt-1">{fieldError('tax_rate_2')}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-white block mb-1" htmlFor="tax-rate-2-label">Rate 2 label</label>
              <input
                id="tax-rate-2-label"
                className={cls('tax_rate_2_label')}
                maxLength={50}
                placeholder={placeholders.label2 ?? 'No label'}
                value={values.tax_rate_2_label}
                onChange={e => set('tax_rate_2_label', e.target.value)}
              />
              {fieldError('tax_rate_2_label') && <p className="text-xs text-red-400 mt-1">{fieldError('tax_rate_2_label')}</p>}
            </div>
          </div>

          {/* A blank rate is ABSENT, not zero. Under a taxing system that makes
              the whole row unresolved and every document under it reads "tax
              rate not configured", which is why the API refuses it and why
              this says so before the round trip. */}
          <p className="text-xs text-fieldiq-text">Blank is not 0%. Enter 0 for no tax.</p>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-fieldiq-border">
          <button onClick={onCancel} disabled={saving} className="flex-1 bg-fieldiq-dark border border-fieldiq-border text-white py-2.5 rounded-xl text-sm font-medium hover:border-fieldiq-muted transition-colors disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={saving} className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-fieldiq-accent hover:bg-amber-400 text-fieldiq-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
            {saving && <Loader2 size={14} className="animate-spin" />}
            {mode === 'correct' ? 'Save correction' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
